/*
 * Sticky add-to-cart bar. Rendered by snippets/sticky-add-to-cart.liquid.
 *
 * This element displays nothing of its own and calculates nothing. It mirrors
 * what the page already renders and delegates its click to the real button:
 *
 *   price     <- #price-<section>            (snippets/price.liquid output)
 *   label     <- #ProductSubmitButton-<sect> (existing locale strings)
 *   thumb     <- the theme's own `variant-change` event
 *   variant   <- the same event
 *   plan line <- subscription-frequency-selector's rendered text
 *   submit    -> that same button's click()
 *
 * Mirroring rather than re-deriving is what keeps it honest: product-info.js
 * already replaces the price block's innerHTML on variant change and
 * product-form.js already rewrites the button's label and disabled state, so
 * both stay correct without this file knowing anything about variants, money
 * formatting or inventory.
 *
 * Known limitation, deliberate: the theme's price block is not selling-plan
 * aware — nothing subscribes to `input[name="selling_plan"]`, so #price- keeps
 * showing the one-time variant price while a subscription is selected. The bar
 * mirrors that faithfully, so it always agrees with the buy box. That is also
 * why the subscription line below is worth showing: it makes the selection the
 * price refers to explicit rather than silent. Making the price itself
 * plan-aware belongs in the price block, not here.
 */
if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        // The snippet renders after the buy_buttons block in source order, but
        // product-info.js can also re-insert this whole subtree during a product
        // swap. Deferring to DOMContentLoaded covers the first case; the
        // immediate path covers every re-connect after load.
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
        } else {
          this.init();
        }
      }

      init() {
        if (this.initialized) return;

        this.form = document.getElementById(this.dataset.formId);
        this.sourcePrice = document.getElementById(this.dataset.priceId);
        this.sourceButton = document.getElementById(this.dataset.submitId);
        this.priceTarget = this.querySelector('[data-sticky-price]');
        this.metaTarget = this.querySelector('[data-sticky-meta]');
        this.image = this.querySelector('.sticky-atc__image');
        this.button = this.querySelector('[data-sticky-submit]');
        this.buttonLabel = this.querySelector('[data-sticky-submit-label]');

        // The buy box to watch is the <product-form> wrapper rather than the
        // <form>, so the error row and dynamic checkout buttons count as part of
        // "the buy box is still on screen".
        this.buyBox = this.form ? this.form.closest('product-form') || this.form : null;

        const missing = [
          [this.form, `form #${this.dataset.formId}`],
          [this.sourcePrice, `price block #${this.dataset.priceId}`],
          [this.sourceButton, `submit button #${this.dataset.submitId}`],
          [this.priceTarget, 'price target'],
          [this.button, 'submit button'],
        ].find(([node]) => !node);

        if (missing) {
          // Nothing to mirror means nothing worth showing. The bar stays hidden
          // (CSS default) rather than appearing empty or with a dead button.
          console.warn(`[sticky-add-to-cart] inert: no ${missing[1]}`);
          return;
        }

        this.initialized = true;

        // Optional: only products with selling plans have a selector to mirror.
        // Matched on form id, not just tag name — product cards elsewhere on the
        // page (complementary products, related products) render their own
        // selectors, and picking the first in the document could easily be one of
        // those rather than this product's.
        this.selector = document.querySelector(
          `subscription-frequency-selector[data-form-id="${this.dataset.formId}"]`
        );
        // Seeded from the server-rendered value so the first sync does not blank
        // the line on a product whose variant title is the only thing to show.
        this.variantTitle = this.metaTarget ? this.metaTarget.textContent.trim() : '';

        this.syncPrice();
        this.syncButton();
        this.syncMeta();

        // characterData catches product-form.js writing a new label via
        // submitButtonText.textContent; childList catches product-info.js
        // swapping the price block's innerHTML wholesale; the class filter
        // catches setUnavailable() hiding the price block.
        this.priceObserver = new MutationObserver(() => this.syncPrice());
        this.priceObserver.observe(this.sourcePrice, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['class'],
        });

        this.buttonObserver = new MutationObserver(() => this.syncButton());
        this.buttonObserver.observe(this.sourceButton, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
        });

        if (this.selector) {
          // The selector signals its state two ways: a class on its host for
          // one-time vs subscribe, and the frequency name's text for which
          // plan. Watching both covers a mode toggle and a frequency change.
          // Strictly read-only — this never writes to the selector, the Recharge
          // widget, or the selling_plan input.
          this.selectorObserver = new MutationObserver(() => this.syncMeta());
          this.selectorObserver.observe(this.selector, {
            subtree: true,
            characterData: true,
            childList: true,
            attributes: true,
            attributeFilter: ['class'],
          });
        }

        // The theme's own variant event, rather than a second fetch or a parse of
        // the variant JSON: product-info.js publishes it with the resolved
        // variant right after it updates the price block.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        }

        this.visibilityObserver = new IntersectionObserver(this.onBuyBoxIntersect.bind(this), { threshold: 0 });
        this.visibilityObserver.observe(this.buyBox);

        this.onClick = this.onClick.bind(this);
        this.button.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        this.priceObserver?.disconnect();
        this.buttonObserver?.disconnect();
        this.selectorObserver?.disconnect();
        this.visibilityObserver?.disconnect();
        this.variantUnsubscriber?.();
        this.button?.removeEventListener('click', this.onClick);
        // A product swap re-connects this element against a fresh DOM, so the
        // observers above have to be rebuilt rather than reused.
        this.initialized = false;
      }

      onBuyBoxIntersect(entries) {
        entries.forEach((entry) => {
          // "Out of view" alone is not enough: on a narrow screen the buy box
          // starts below the fold, so a bare !isIntersecting test would show the
          // bar on page load. Requiring the buy box to be *above* the viewport
          // means the bar only ever appears after it has been scrolled past.
          const viewportTop = entry.rootBounds ? entry.rootBounds.top : 0;
          const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom <= viewportTop;
          this.classList.toggle('sticky-atc--visible', scrolledPast);
        });
      }

      /** Copies the main price block verbatim — no parsing, no reformatting. */
      syncPrice() {
        this.priceTarget.innerHTML = this.sourcePrice.innerHTML;
        this.priceTarget.classList.toggle('hidden', this.sourcePrice.classList.contains('hidden'));
      }

      /**
       * Copies the native button's label and availability. The label comes from
       * its first span, the same node product-form.js treats as the button text,
       * so "Sold out" and "Unavailable" arrive already translated.
       */
      syncButton() {
        const disabled =
          this.sourceButton.hasAttribute('disabled') || this.sourceButton.getAttribute('aria-disabled') === 'true';

        this.button.disabled = disabled;
        this.button.setAttribute('aria-disabled', String(disabled));

        const label = this.sourceButton.querySelector('span');
        if (label && this.buttonLabel) {
          // The buy box can be configured to print the price inside its own
          // label ("Add to cart, $16.99"). The bar already shows that price in
          // its own column, so the copy drops it rather than saying it twice.
          // cloneNode keeps the source button untouched.
          const copy = label.cloneNode(true);
          copy.querySelector('.product-form__submit-price')?.remove();
          this.buttonLabel.textContent = copy.textContent.trim();
        }
      }

      onVariantChange({ data: { variant } }) {
        if (!variant) return;

        // Suppressed for a product whose only "variant" is Shopify's
        // "Default Title" placeholder, which says nothing to a shopper.
        this.variantTitle = this.hasAttribute('data-hide-variant-title') ? '' : variant.title || '';
        this.syncMeta();
        this.syncImage(variant);
      }

      syncImage(variant) {
        const preview = variant.featured_media?.preview_image;
        if (!this.image || !preview?.src) return;

        // preview_image.src is a bare CDN url, so it needs the same width param
        // the Liquid image_url filter applied to the initial render — otherwise
        // the swap pulls the full-size original.
        const separator = preview.src.includes('?') ? '&' : '?';
        this.image.src = `${preview.src}${separator}width=160`;
        this.image.removeAttribute('srcset');
        if (variant.featured_media.alt) this.image.alt = variant.featured_media.alt;
      }

      /**
       * The line under the title: what is currently selected. Both halves are
       * copied from text the page already renders, so nothing here introduces a
       * string that would need translating.
       */
      syncMeta() {
        if (!this.metaTarget) return;

        const parts = [this.variantTitle, this.selectionSummary()].filter((part) => part);
        // A dot is punctuation, not copy — nothing to translate.
        this.metaTarget.textContent = parts.join(' · ');
        this.metaTarget.hidden = parts.length === 0;
      }

      /**
       * Reads the subscription selector's own rendered text. When subscribed
       * that is its summary line ("Ships every 4 weeks"); when one-time it is
       * the one-time panel's title. Returns '' when the product has no selector.
       */
      selectionSummary() {
        if (!this.selector) return '';

        const source = this.selector.classList.contains('sfs--onetime')
          ? this.selector.querySelector('.sfs__panel--onetime .sfs__toggle-title')
          : this.selector.querySelector('[data-sfs-summary]');

        // collapse whitespace: the summary spans two lines of Liquid source, so
        // its textContent arrives with the indentation between them.
        return source ? source.textContent.replace(/\s+/g, ' ').trim() : '';
      }

      /**
       * Clicks the real submit button rather than submitting the form directly.
       * That keeps the submitter identical to a shopper's own click, so
       * product-form.js's handler, its FormData (id, quantity, selling_plan and
       * every line item property), and anything else listening on that button all
       * behave exactly as they do in the buy box. No second submission path
       * exists here.
       */
      onClick(event) {
        event.preventDefault();
        if (this.button.disabled) return;
        this.sourceButton.click();
      }
    }
  );
}
